import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { MapPin, Plus, ChevronLeft } from 'lucide-react';
const LazyVerificationBadge = React.lazy(() => import('../components/VerificationBadge'));
import VenueDetailModal from '../components/VenueDetailModal';
import CreateVenueModal from '../components/CreateVenueModal';
import { API_URL } from '../config/api';

type Venue = {
  _id: string;
  name: string;
  location: { name?: string; city?: string; address?: string; state?: string; country?: string };
  capacity: { max: number };
  owner: string | { _id: string; username?: string; avatar?: string };
  status: 'available' | 'booked';
  available?: boolean;
  category?: string;
  aboutShort?: string;
  description?: string;
  images?: string[];
  createdAt?: string;
};

const API = API_URL.replace(/\/api$/, '');

export default function DiscoverVenues({ token, onBack }: { token: string | null; onBack?: () => void }) {
  const [q, setQ] = useState(() => {
    try { return localStorage.getItem('auralink-venues-search-q') || ''; } catch { return ''; }
  });
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  // Future filters (hidden for now)
  const city = '';
  const minCap = '';
  const maxCap = '';
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Venue[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [actionVenue, setActionVenue] = useState<Venue | null>(null);
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(10);
  const [sessionSeed] = useState(() => (Date.now() ^ Math.floor(Math.random() * 1e9)) >>> 0);
  const [venueTierById, setVenueTierById] = useState<Record<string, string>>({});

  const debounced = useMemo(() => ({ q, city, minCap, maxCap, onlyAvailable }), [q, city, minCap, maxCap, onlyAvailable]);

  function primaryImage(v: Venue) {
    return Array.isArray(v.images) && v.images.length > 0 ? v.images[0] : undefined;
  }

  function scoreVenue(v: Venue, seed: number) {
    const ta = v.createdAt ? dayjs(v.createdAt).valueOf() : 0;
    const ageHours = Math.max(0, (Date.now() - ta) / (1000 * 60 * 60));
    const recency = 1 / (1 + ageHours / 24);
    const imageBoost = primaryImage(v) ? 0.15 : 0;
    const availBoost = v.available || v.status === 'available' ? 0.15 : 0;
    const cap = v.capacity?.max || 0;
    const capScore = Math.min(1, cap / 200); // soft boost up to 200
    // simple seeded noise
    const id = String(v._id || v.name || '') + ':' + seed;
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) h = (h ^ id.charCodeAt(i)) * 16777619;
    const noise = ((h >>> 0) % 1000) / 1000; // 0..1
    return recency * 0.55 + imageBoost + availBoost + capScore * 0.1 + noise * 0.1;
  }

  function seededShuffle<T>(arr: T[], seed: number): T[] {
    // Linear Congruential Generator for deterministic shuffle
    let s = seed || 1;
    const rand = () => (s = (s * 1664525 + 1013904223) % 0xffffffff) / 0xffffffff;
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  async function fetchPage(reset = false) {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const nextPage = reset ? 1 : page + 1;
      const params: any = { page: reset ? 1 : nextPage, limit: 12 };
      if (debounced.q.trim()) params.q = debounced.q.trim();
      if (debounced.city.trim()) params.city = debounced.city.trim();
      if (debounced.minCap.trim()) params.capacityMin = debounced.minCap.trim();
      if (debounced.maxCap.trim()) params.capacityMax = debounced.maxCap.trim();
      if (debounced.onlyAvailable) params.onlyAvailable = 'true';
      const res = await axios.get(`${API_URL}/venues/search`, { params });
      const list: Venue[] = res.data?.venues || [];
      // Combine a quality-based score with a seeded shuffle for fair rotation
      const seedNow = (Date.now() ^ sessionSeed) >>> 0;
      const scored = list
        .slice()
        .sort((a, b) => scoreVenue(b, seedNow) - scoreVenue(a, seedNow));
      const randomized = seededShuffle(scored, seedNow);
      if (reset) {
        setItems(randomized);
        setPage(1);
      } else {
        setItems((arr) => [...arr, ...randomized]);
        setPage(nextPage);
      }
      const totalPages = res.data?.totalPages || 1;
      const current = reset ? 1 : nextPage;
      setHasMore(current < totalPages);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { fetchPage(true); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  useEffect(() => {
    // Clear one-time deep-link search seed
    try { localStorage.removeItem('auralink-venues-search-q'); } catch {}
  }, []);

  async function requestBooking(venueId: string) {
    if (!token) {
      setError('You must be logged in to request a booking');
      return;
    }
    try {
      const target = items.find((v) => v._id === venueId);
      const name = target?.name || 'this venue';
      const ok = typeof window !== 'undefined' ? window.confirm(`Send a booking request for ${name}?`) : true;
      if (!ok) return;
      await axios.post(`${API_URL}/booking-requests/create`, { venueId }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Booking request created. Check chat for negotiation.');
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to create booking request');
    }
  }

  const filteredByNearMe = useMemo(() => {
    const base = items;
    if (nearMeEnabled && userLoc) {
      return base.filter((v) => {
        const c = (v as any)?.location?.coordinates;
        if (!c || typeof c.lat !== 'number' || typeof c.lng !== 'number') return false;
        return haversineKm(userLoc.lat, userLoc.lng, c.lat, c.lng) <= radiusKm;
      });
    }
    return base;
  }, [items, nearMeEnabled, userLoc, radiusKm]);

  function deepMatch(v: Venue, query: string) {
    if (!query.trim()) return true;
    const ql = query.toLowerCase().trim();
    const tokens = ql.split(/\s+/).filter(Boolean);
    const fields: string[] = [];
    fields.push(v.name || '');
    if (v.description) fields.push(v.description);
    if (typeof v.owner === 'object') fields.push(v.owner?.username || '');
    const loc = v.location || {};
    fields.push(loc.name || '', loc.city || '', loc.address || '', loc.state || '', loc.country || '');
    const text = fields.join(' \u2022 ').toLowerCase();
    // All tokens should be present for a "deep" match
    return tokens.every((t) => text.includes(t));
  }

  const displayItems = useMemo(() => {
    const base = filteredByNearMe;
    if (!q.trim()) return base;
    return base.filter((v) => deepMatch(v, q));
  }, [filteredByNearMe, q]);

  // Fetch verification status for venues shown
  useEffect(() => {
    const ids = displayItems.map((v) => v._id);
    const missing = ids.filter((id) => !(id in venueTierById));
    if (!missing.length) return;
    (async () => {
      try {
        const results = await Promise.all(
          missing.map((id) => axios.get(`${API_URL}/verifications/status/venue/${id}`).catch(() => null))
        );
        const updates: Record<string, string> = {};
        missing.forEach((id, idx) => {
          const r = results[idx];
          const tier = r && r.data ? (r.data.tier || 'none') : 'none';
          updates[id] = tier;
        });
        setVenueTierById((prev) => ({ ...prev, ...updates }));
      } catch {}
    })();
  }, [displayItems]);

  return (
    <div className="min-h-screen themed-page">
      <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => {
                if (onBack) onBack();
                else if (window.history.length > 1) window.history.back();
                else try { (window as any).navigateTo?.('discover'); } catch {}
              }}
              className="flex items-center text-theme-secondary hover:text-heading mb-2 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Discover
            </button>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Discover Venues</h2>
            <p className="mt-1 text-[13px] sm:text-sm leading-relaxed text-theme-secondary max-w-prose">Find and request bookings.</p>
            <div className="mt-3 rounded-xl border p-3 text-xs sm:text-sm text-theme-secondary max-w-prose" style={{ borderColor: 'var(--border)' }}>
              How tokens work: Send a booking request to a venue. Once the venue approves and payment is confirmed, you’ll receive a one‑time booking token in the chat. Use that token when creating your event to link it to the venue. Tokens expire and can be revoked by the venue.
            </div>
          </div>
        </div>

        {/* Posts-style single search bar (no left toggle) */}
        <div className="-mx-3 sm:mx-0">
          <div className="p-[1px] bg-gradient-to-r from-cyan-400 to-purple-500 rounded-none sm:rounded-xl">
            <div className="flex items-center gap-0.5 themed-card rounded-none sm:rounded-xl px-1 py-0 w-full flex-nowrap" style={{ background: 'var(--card)' }}>
              <input
                type="text"
                className="input h-8 text-sm flex-1 min-w-0 rounded-md bg-white/60 dark:bg-slate-800/60 focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder:text-theme-secondary"
                placeholder="Search venues"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search venues"
              />
              <button
                onClick={() => setShowCreateVenue(true)}
                aria-label="Create venue"
                className="p-0.5 rounded-md transition-all flex-none bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20"
                title="Create venue"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Near Me controls (restyled for responsiveness) */}
        <div className="themed-card rounded-xl p-3 sm:p-4 border" style={{ borderColor: 'var(--border)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  if (nearMeEnabled) {
                    setNearMeEnabled(false);
                    setUserLoc(null);
                    return;
                  }
                  try {
                    const pos = await getCurrentPosition();
                    setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                    setNearMeEnabled(true);
                  } catch (e) {
                    console.warn('Geolocation error', e);
                    alert('Unable to get your location. Please allow location access.');
                  }
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/60 ${nearMeEnabled ? 'bg-indigo-600 text-white border-indigo-600' : ''}`}
                style={{ borderColor: 'var(--border)' }}
                aria-pressed={nearMeEnabled}
                aria-label="Toggle near me filter"
              >
                <MapPin className="w-4 h-4" /> {nearMeEnabled ? 'Near Me Enabled' : 'Use My Location'}
              </button>
            </div>

            <div className="w-full sm:w-auto sm:min-w-[340px]">
              <label htmlFor="radiusRange" className="block text-xs sm:text-sm text-theme-secondary mb-1">Search radius</label>
              <div className="flex items-center gap-3">
                <input
                  id="radiusRange"
                  type="range"
                  min={1}
                  max={50}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  aria-label="Near me radius (km)"
                  className="flex-1"
                  style={{ accentColor: nearMeEnabled ? '#4f46e5' : undefined }}
                  disabled={!nearMeEnabled}
                />
                <span
                  className={`px-2 py-1 text-xs rounded-md border whitespace-nowrap ${nearMeEnabled ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-theme-secondary'}`}
                  style={{ borderColor: 'var(--border)' }}
                  aria-live="polite"
                >
                  {radiusKm} km
                </span>
              </div>
              {!nearMeEnabled && (
                <div className="mt-1 text-xs text-theme-secondary">Enable Near Me to adjust the radius</div>
              )}
            </div>
          </div>
          {nearMeEnabled && userLoc && (
            <div className="mt-2 text-xs text-theme-secondary">Filtering venues within {radiusKm} km of your location</div>
          )}
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayItems.map((v) => {
            const img = primaryImage(v);
            const city = v.location?.city || v.location?.name || '';
            const ABOUT_CARD_MAX = 140;
            const aboutRaw = (v.aboutShort || '').trim();
            const aboutDisplay = aboutRaw.length > ABOUT_CARD_MAX ? (aboutRaw.slice(0, ABOUT_CARD_MAX - 1) + '…') : aboutRaw;
            return (
              <div
                key={v._id}
                className="group cursor-pointer"
                onClick={() => setActionVenue(v)}
                role="button"
                aria-label={`View venue ${v.name}`}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActionVenue(v); } }}
              >
                <div className="p-[1px] rounded-2xl bg-gradient-to-r from-cyan-400/60 to-purple-500/60 group-hover:from-cyan-400 group-hover:to-purple-500 transition-colors">
                  <div className="themed-card rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <div className="relative w-full h-44 sm:h-52 overflow-hidden bg-gradient-to-br from-slate-200/50 to-slate-300/30 dark:from-slate-800/40 dark:to-slate-900/40">
                      {img ? (
                        <img src={img} alt={v.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm text-theme-secondary">No image</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-transparent" />
                      <div className="absolute top-2 right-2">
                        <span className={`badge ${v.status === 'available' ? 'badge-accent' : 'badge-amber'}`}>{v.status}</span>
                      </div>
                    </div>
                    <div className="p-3 sm:p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-lg font-semibold text-heading leading-tight break-words">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="min-w-0 flex-1 truncate">{v.name}</span>
                            </div>
                          </div>
                          <div className="text-sm text-theme-secondary inline-flex items-center gap-1 mt-0.5">
                            <MapPin className="w-4 h-4" /> {city || 'Location TBA'}
                          </div>
                          {aboutDisplay && (
                            <div className="mt-1 text-xs text-theme-secondary line-clamp-2">{aboutDisplay}</div>
                          )}
                          {v.category && (
                            <div className="mt-1">
                              <span className="badge text-xs capitalize">{String(v.category).replace(/-/g, ' ')}</span>
                            </div>
                          )}
                          <div className="pt-1">
                            <React.Suspense fallback={null}>
                              <LazyVerificationBadge kind="venue" tier={venueTierById[v._id] as any} />
                            </React.Suspense>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-theme-secondary">Capacity: {v.capacity?.max ?? '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center pt-2">
          {hasMore ? (
            <button className="px-4 py-2 rounded-xl border text-sm" style={{ borderColor: 'var(--border)' }} onClick={() => fetchPage(false)} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          ) : (
            <span className="text-sm text-theme-secondary">No more results</span>
          )}
        </div>
      </div>

      {selectedVenue && (
        <VenueDetailModal venue={selectedVenue as any} token={token} onClose={() => setSelectedVenue(null)} />
      )}
      {actionVenue && (
        <ActionModal
          venue={actionVenue}
          onClose={() => setActionVenue(null)}
          onView={() => { setSelectedVenue(actionVenue); setActionVenue(null); }}
          onRequest={() => { requestBooking(actionVenue._id); setActionVenue(null); }}
        />
      )}
      {showCreateVenue && (
        <CreateVenueModal
          isOpen={showCreateVenue}
          token={token}
          onClose={() => setShowCreateVenue(false)}
          editVenue={null}
          onCreated={() => { setShowCreateVenue(false); fetchPage(true); }}
          onSaved={() => { setShowCreateVenue(false); fetchPage(true); }}
        />
      )}
    </div>
  );
}

function getCurrentPosition(opts?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number { return deg * Math.PI / 180; }

function ActionModal({ venue, onClose, onView, onRequest }: { venue: Venue; onClose: () => void; onView: () => void; onRequest: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:w-[420px] p-4 sm:p-6 rounded-t-2xl sm:rounded-2xl themed-card shadow-xl">
        <div className="mb-3">
          <div className="text-base sm:text-lg font-semibold">{venue.name}</div>
          <div className="text-xs sm:text-sm text-theme-secondary">Choose an action</div>
        </div>
        <div className="flex flex-col gap-2">
          <button className="btn w-full justify-center" onClick={onView}>View Venue</button>
          <button
            className="w-full px-4 py-2 rounded-xl border hover:bg-emerald-500/10 hover:border-emerald-500/40 transition-colors"
            style={{ borderColor: 'var(--border)' }}
            onClick={onRequest}
          >
            Request Booking
          </button>
        </div>
        <button className="mt-3 w-full text-sm text-theme-secondary hover:text-heading" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
